import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  CommonService,
  CONSTANT,
  FileUploadService,
  LoaderService,
  ObservableService,
  OrderService,
} from '@bst-frontend/shared';
import { finalize } from 'rxjs';

export type ReadyDispatchMode = 'create' | 'edit' | 'view';

export interface Product {
  productId: string;
  code: string;
  name: string;
}

export interface RequestedItem {
  product: Product;
  qty: number;
  weight: number;
}

export interface ProductDifferencesDto {
  requested: RequestedItem[];
  // loaded / dispatched / delivered can exist but are not needed here
}

export interface UploadedFile {
  id: string; // use fileKey from S3
  name: string;
  url: string; // public URL
  mimeType: string;
  size?: number;
}

export interface DifferencePayloadItem {
  product: Product;
  subItem: Product;
  changeInSubItem: Product;
  qty: number;
  changeInQty: number;
  weight: number;
  changeInWeight: number;
}

export interface FilePayload {
  path: string;
  name: string;
  id: string;
  size?: number;
}

export interface ReadyDispatchPayload {
  differences: DifferencePayloadItem[];
  loadingImages: FilePayload[];
  deliveryImages: FilePayload[];
}

/**
 * Optional: for edit/view we can receive backend payload to pre-fill
 */

@Component({
  selector: 'bst-frontend-deliver-modal',
  templateUrl: './deliver-modal.component.html',
  styleUrls: ['./deliver-modal.component.css'],
})
export class DeliverModalComponent {
  /** create | edit | view */
  @Input() mode: ReadyDispatchMode = 'create';

  @Input() orderData: any;
  @Input() subOrder?: any;

  /** Product differences coming from parent */
  @Input() productDifferences!: any;

  /** Optional existing payload for edit/view */
  @Input() initialData?: any;

  dispatchForm!: FormGroup;

  // upload state
  loadingFiles: UploadedFile[] = [];
  weightSlipFiles: UploadedFile[] = [];

  // dropdown options for pickup product
  productOptions: Product[] = [];

  isSubmitting = false;

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private fileUploadService: FileUploadService,
    private loader: LoaderService,
    private orderService: OrderService,
    private observableService: ObservableService,
    public commonService: CommonService,
    @Inject('environment') private environment: any
  ) {}

  ngOnInit(): void {
    if (!this.productDifferences || !this.productDifferences.dispatched) {
      throw new Error('productDifferences.dispatched is required');
    }

    this.productOptions = this.productDifferences.dispatched.map(
      (r: any) => r.product
    );

    this.buildForm();
    this.buildItemsFromRequested();
    this.initFilesFromInitialData();

    if (this.mode === 'view') {
      this.dispatchForm.disable({ emitEvent: false });
    }
  }

  // ---------- FORM BUILDING ----------

  private buildForm(): void {
    this.dispatchForm = this.fb.group({
      items: this.fb.array([]),
    });
  }

  get items(): FormArray {
    return this.dispatchForm.get('items') as FormArray;
  }

  private buildItemsFromRequested(): void {
    const requested = this.productDifferences.dispatched;

    requested.forEach((req: any) => {
      const existingDiff = this.findExistingDifference(req.product);
      const requestedQty = req.qty;
      const requestedWeight = req.weight;

      // pickup (what user can modify)
      const pickupQty = existingDiff != null ? existingDiff.qty : req.qty;
      const pickupWeight =
        existingDiff != null ? existingDiff.weight : req.weight;
      const pickupProduct =
        existingDiff != null ? existingDiff.subItem : req.product;

      this.items.push(
        this.fb.group({
          product: [req.product], // original product
          requestedQty: [requestedQty],
          requestedWeight: [requestedWeight],

          // pickupProduct: [pickupProduct, Validators.required],
          pickupQty: [pickupQty, [Validators.required]],
          pickupWeight: [pickupWeight, [Validators.required]],
        })
      );
    });
  }

  private findExistingDifference(
    product: Product
  ): DifferencePayloadItem | undefined {
    if (!this.initialData || !this.initialData?.delivered?.length) {
      return undefined;
    }
    return this.initialData?.delivered.find(
      (d: any) => d.product.productId == product.productId
    );
  }

  // ---------- FILES INIT (EDIT / VIEW) ----------

  private initFilesFromInitialData(): void {
    if (!this.subOrder) return;

    const mapInputFiles = (input?: FilePayload[]): UploadedFile[] =>
      (input || []).map((f) => ({
        id: f.path,
        name: f.name,
        url: this.environment.s3UrlDownload + '/' + f.path,
        path: f.path,
        mimeType: '', // you can’t know this from backend unless it sends
        size: f.size,
      }));

    this.loadingFiles = mapInputFiles(this.subOrder?.deliveryImages);
    this.weightSlipFiles = mapInputFiles(this.subOrder?.proofOfDelivery);
  }

  // ---------- TEMPLATE HELPERS ----------

  compareProducts(a: Product | null, b: Product | null): boolean {
    return !!a && !!b && a.productId === b.productId;
  }

  isImage(file: UploadedFile): boolean {
    const mime = (file.mimeType || '').toLowerCase();

    // if mimeType is set and says image → it's an image
    if (mime.startsWith('image/')) {
      return true;
    }

    // fallback: check extension in url or name
    const nameOrUrl = (file.url || file.name || '').toLowerCase();
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(nameOrUrl);
  }

  // ---------- FILE UPLOAD / REMOVE / PREVIEW ----------

  onFileSelected(event: Event, type: 'loading' | 'weightSlip') {
    if (this.mode === 'view') return;

    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const files = Array.from(input.files);
    this.loader.show();
    files.forEach((file) => {
      const documentType = this.getDocumentType(type);

      this.fileUploadService
        .uploadFileToS3(file, file.name, documentType, this.orderData._id) // ← generic upload from earlier
        .pipe(
          finalize(() => {
            this.loader.hide();
          })
        )
        .subscribe({
          next: (info: any) => {
            const uploaded: UploadedFile = {
              id: info.fileKey,
              name: file.name,
              url: info.fileUrl,
              mimeType: file.type,
              size: file.size,
            };

            if (type === 'loading') {
              this.loadingFiles.push(uploaded);
            } else if (type === 'weightSlip') {
              this.weightSlipFiles.push(uploaded);
            }
          },
          error: (err) => {
            console.error('Upload failed', err);
          },
        });
    });

    input.value = '';
  }

  private getDocumentType(type: 'loading' | 'weightSlip'): string {
    switch (type) {
      case 'loading':
        return 'delivery _image';
      case 'weightSlip':
        return 'proof_of_delivery';
      default:
        return 'general_document';
    }
  }

  onRemoveFile(type: 'loading' | 'weightSlip', file: UploadedFile): void {
    if (this.mode === 'view') return;

    if (type === 'loading') {
      this.loadingFiles = this.loadingFiles.filter((f) => f.id !== file.id);
    } else if (type === 'weightSlip') {
      this.weightSlipFiles = this.weightSlipFiles.filter(
        (f) => f.id !== file.id
      );
    }
    // if you later implement direct S3 delete, call it here
  }

  onPreviewFile(file: UploadedFile): void {
    window.open(file.url, '_blank');
  }

  // ---------- PAYLOAD MAPPERS ----------

  private buildDifferences(): DifferencePayloadItem[] {
    return this.items.controls.map((ctrl) => {
      const v = ctrl.value;

      const product: Product = v.product;
      const subItem: Product = v.pickupProduct;

      const qty: number = v.pickupQty;
      const pickupQty: number = v.requestedQty;

      const weight: number = v.pickupWeight;
      const pickupWeight: number = v.requestedWeight;

      return {
        product,
        subItem,
        changeInSubItem: subItem, // if BE expects something else, change here

        qty,
        changeInQty: qty - pickupQty,

        weight,
        changeInWeight: weight - pickupWeight,
      };
    });
  }

  private mapFilesForPayload(files: UploadedFile[]): FilePayload[] {
    return files.map((f) => ({
      id: f.url,
      path: f.id,
      name: f.name,
      size: f.size,
    }));
  }

  // ---------- MODAL ACTIONS ----------

  cancel(): void {
    this.activeModal.dismiss('cancel');
  }

  async onSubmit() {
    if (this.mode === 'view') {
      this.activeModal.close('view-close');
      return;
    }

    if (this.dispatchForm.invalid) {
      this.dispatchForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;

    const payload: any = {
      differences: this.buildDifferences(),
      deliveryImages: this.mapFilesForPayload(this.loadingFiles),
      proofOfDelivery: this.mapFilesForPayload(this.weightSlipFiles),
    };

    this.loader.show();
    try {
      await this.orderService
        .updateSubOrder(
          CONSTANT.orderStatus['DELIVERED'],
          this.subOrder._id,
          payload
        )
        .toPromise();
      this.observableService.send({ refresh: true });
      // this.loader.hide();
    } catch (error) {
      console.log('Error', error);
    }
    this.activeModal.close({ status: 'saved' });
    this.isSubmitting = false;
  }
}
